
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, TrendingUp, Leaf, User, LogOut, CreditCard, AlertCircle } from 'lucide-react';
import FastFilterPanel from './FastFilterPanel';
import SubscriptionManager from './SubscriptionManager';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { FEATURE_FLAGS } from '../utils/config';
// Utility function to capitalize words
const capitalizeWords = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Constants to prevent recreation
const DEFAULT_FILTERS = { weights: [], types: [], brands: [], strains: [], dispensaries: [], states: [] };
const DEFAULT_LOCATION = { zipcode: '', radius: 25 };


// Base sort options available for all product types
const BASE_SORT_OPTIONS = [
  { value: 'cost_asc', label: 'Price: Low to High' },
  { value: 'cost_desc', label: 'Price: High to Low' }
];

// Additional sort options only for flower products
const FLOWER_SORT_OPTIONS = [
  { value: 'tac_desc', label: 'Highest Potency (TAC)' },
  { value: 'thc_desc', label: 'Highest THC' }
];
const TABS = [
  { id: 'flower', label: 'All Flower', icon: Leaf },
  { id: 'concentrates', label: 'All Concentrates', icon: TrendingUp }
];

// Utility functions
const formatPrice = (price) => `$${parseFloat(price).toFixed(2)}`;
const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
const getRankBadge = (index) => {
  if (index === 0) return '🥇';
  if (index === 1) return '🥈';
  if (index === 2) return '🥉';
  return index + 1;
};

// Safe error handling - never expose sensitive information
const getSafeErrorMessage = (error) => {
  // Always return a generic, user-friendly message
  return "I'm sorry, we're having trouble right now. If this continues, please contact support@weederboard.com";
};

// Memoized Pagination Buttons component
const PaginationButtons = React.memo(({ currentPage, totalPages, onPageChange, loading }) => {
  const buttons = useMemo(() => {
    const maxButtons = 5;
    const startPage = Math.max(1, Math.min(totalPages - maxButtons + 1, currentPage - 2));
    const endPage = Math.min(totalPages, startPage + maxButtons - 1);
    
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }, [currentPage, totalPages]);
  
  return (
    <div className="flex items-center space-x-2">
      {buttons.map(pageNum => (
        <button
          key={pageNum}
          onClick={() => onPageChange(pageNum)}
          disabled={loading}
          className={`px-3 py-2 rounded-lg font-medium transition-colors ${
            currentPage === pageNum
              ? 'bg-green-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {pageNum}
        </button>
      ))}
    </div>
  );
});


// Memoized User Account Menu component
const UserAccountMenu = React.memo(({ user, onManageSubscription, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleManageSubscription = useCallback(() => {
    setIsOpen(false);
    onManageSubscription();
  }, [onManageSubscription]);

  const handleLogout = useCallback(() => {
    setIsOpen(false);
    onLogout();
  }, [onLogout]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.user-menu')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (!user) return null;

  return (
    <div className="relative user-menu">
      <button
        onClick={handleToggle}
        className="flex items-center space-x-3 bg-white rounded-xl px-4 py-3 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
      >
        <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-medium">
          <User size={16} />
        </div>
        <div className="text-left hidden sm:block">
          <p className="text-sm font-medium text-gray-900">
            {user.firstName || user.email?.split('@')[0] || 'User'}
          </p>
          <p className="text-xs text-green-600 font-medium">Premium</p>
        </div>
        <ChevronDown 
          size={16} 
          className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
            <p className="text-sm text-gray-500">{user.email}</p>
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                Premium Active
              </span>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={handleManageSubscription}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <CreditCard size={16} className="text-gray-400" />
              <div>
                <p className="font-medium">Manage Subscription</p>
                <p className="text-xs text-gray-500">Billing & account settings</p>
              </div>
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 text-left text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={16} className="text-red-500" />
              <div>
                <p className="font-medium">Sign Out</p>
                <p className="text-xs text-red-500">Log out of your account</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

// Memoized Leaderboard Item component
const LeaderboardItem = React.memo(({ item, index }) => {
  const rankBadge = useMemo(() => getRankBadge(index), [index]);
  const formattedPrice = useMemo(() => formatPrice(item.cost), [item.cost]);
  const formattedDate = useMemo(() => formatDate(item.scraped_at), [item.scraped_at]);
  
  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        {/* Left side - Rank, Image, and Product Info */}
        <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
              {rankBadge}
            </div>
          </div>
          
          {/* Product Image */}
          {item.image_url && (
            <div className="flex-shrink-0">
              <img 
                src={item.image_url} 
                alt={`${item.strain} by ${item.brand}`}
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-cover border border-gray-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-base sm:text-lg leading-tight">{item.strain}</h3>
            <p className="text-gray-600 font-medium text-sm sm:text-base">{item.brand}</p>
            <p className="text-xs sm:text-sm text-gray-500 truncate">{item.origin_dispensary}</p>
            <div className="flex flex-wrap gap-1 sm:gap-2 mt-2">
              <span className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs sm:text-sm font-medium">
                {item.type || 'Flower'}
              </span>
              <span className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm font-medium">
                {item.weight}
              </span>
              {item.thc_percentage && (
                <span className="px-2 sm:px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-medium">
                  {item.thc_percentage}% THC
                </span>
              )}
              {item.tac_percentage && (
                <span className="px-2 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs sm:text-sm font-medium">
                  {item.tac_percentage}% TAC
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Right side - Price and Action */}
        <div className="flex flex-col sm:items-end space-y-2 sm:min-w-0">
          <div className="text-xl sm:text-2xl font-bold text-green-600">{formattedPrice}</div>
          <div className="text-xs sm:text-sm text-gray-500">{formattedDate}</div>
          {item.product_link && (
            <button
              onClick={() => window.open(item.product_link, '_blank', 'noopener,noreferrer')}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
            >
              Order Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.item.id === nextProps.item.id && prevProps.index === nextProps.index;
});

const CannabisLeaderboard = () => {
  // Auth context
  const { user, isAuthenticated, logout } = useAuth();
  
  // Core state
  const [activeTab, setActiveTab] = useState('flower');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state - limit based on subscription
  const [limit, setLimit] = useState(20); // All users are premium
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter states
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [location, setLocation] = useState(DEFAULT_LOCATION);
  const [sortBy, setSortBy] = useState('cost_asc');
  
  // Add a flag to track if preferences have been loaded
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  
  // UI state
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [showSubscriptionManager, setShowSubscriptionManager] = useState(false);
  
  
  // Memoized sort options based on active tab
  const availableSortOptions = useMemo(() => {
    if (activeTab === 'flower') {
      return [...BASE_SORT_OPTIONS, ...FLOWER_SORT_OPTIONS];
    }
    return BASE_SORT_OPTIONS;
  }, [activeTab]);




  // Build API endpoint and parameters
  const buildApiParams = useCallback((tab, filters, location, sortBy, limit, page) => {
    const params = {
      limit: limit.toString(),
      page: page.toString(),
      ...(sortBy && { sort_by: sortBy })
    };
    
    // Add filter parameters
    if (filters.weights.length > 0) params.weights = filters.weights.join(',');
    if (filters.types.length > 0) params.types = filters.types.join(',');
    if (filters.brands.length > 0) params.brands = filters.brands.join(',');
    if (filters.strains.length > 0) params.strains = filters.strains.join(',');
    if (filters.dispensaries.length > 0) params.dispensaries = filters.dispensaries.join(',');
    if (filters.states.length > 0) params.states = filters.states.join(',');
    
    // Add location parameters
    if (location.zipcode && location.zipcode.trim() !== '') {
      params.zipcode = location.zipcode.trim();
      params.radius = location.radius.toString();
    }
    
    // Build endpoint based on active tab and landing page status
    let endpoint;
    const usePublicEndpoints = !FEATURE_FLAGS.landingPageActive;
    
    switch (tab) {
      case 'flower':
        endpoint = usePublicEndpoints ? '/test/leaderboard/flower' : '/leaderboard/flower';
        break;
      case 'concentrates':
        endpoint = usePublicEndpoints ? '/test/leaderboard/concentrates' : '/leaderboard/concentrates';
        break;
      default:
        endpoint = usePublicEndpoints ? '/test/leaderboard/flower' : '/leaderboard/flower';
    }
    
    return { endpoint, params };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const { endpoint, params } = buildApiParams(activeTab, filters, location, sortBy, limit, page);
    
    try {
      const result = await apiClient.get(endpoint, params);
      
      if (result.success) {
        // Capitalize brand names in the data
        const processedData = result.data.map(item => ({
          ...item,
          brand: capitalizeWords(item.brand)
        }));
        setData(processedData);
        setTotalCount(result.total || result.count);
        setTotalPages(result.total_pages || Math.ceil((result.total || result.count) / limit));
      } else {
        setError(result.error || 'Failed to fetch data');
        setData([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (err) {
      // Always use safe error message to prevent exposing sensitive information
      setError(getSafeErrorMessage(err));
      
      setData([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, location, sortBy, limit, page, buildApiParams]);

  // Tab switching - DON'T reset filters, let preferences handle it
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    // Don't reset filters - let preferences be applied
    // Only reset sort if needed for concentrates
    if (tabId !== 'flower' && (sortBy === 'thc_desc' || sortBy === 'tac_desc')) {
      setSortBy('cost_asc');
    }
    setPage(1);
  }, [sortBy]);
  
  const handleLimitChange = useCallback((newLimit) => {
    setLimit(newLimit);
    setPage(1);
  }, []);
  
  const handleSortChange = useCallback((newSort) => {
    setSortBy(newSort);
    setPage(1);
  }, []);
  
  // Location handlers
  const handleLocationChange = useCallback((newLocation) => {
    setLocation(newLocation);
    setPage(1);
  }, []);
  
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);
  
  // Filter handlers
  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);
  
  const clearAllFilters = useCallback(() => {
    const newFilters = DEFAULT_FILTERS;
    setFilters(newFilters);
    setPage(1);
  }, []);
  
  const toggleFilters = useCallback(() => {
    setFiltersExpanded(prev => !prev);
  }, []);
  
  // Handle subscription management
  const handleManageSubscription = useCallback(() => {
    setShowSubscriptionManager(true);
  }, []);

  const handleCloseSubscriptionManager = useCallback(() => {
    setShowSubscriptionManager(false);
  }, []);

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      await logout();
      // Optionally redirect to home page or show a message
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [logout]);

  // Load user preferences on mount AND when activeTab changes
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!isAuthenticated || !user) {
        setPreferencesLoaded(true); // Mark as loaded even if not authenticated
        return;
      }
      
      try {
        // Pass activeTab as product_type parameter
        const result = await apiClient.get('/user/preferences', { product_type: activeTab });
        
        if (result.success && result.data) {
          const prefs = result.data;
          
          // Apply preferences to state - always set filters even if empty
          setFilters({
            weights: Array.isArray(prefs.weights) ? prefs.weights : [],
            types: Array.isArray(prefs.types) ? prefs.types : [],
            brands: Array.isArray(prefs.brands) ? prefs.brands : [],
            strains: Array.isArray(prefs.strains) ? prefs.strains : [],
            dispensaries: Array.isArray(prefs.dispensaries) ? prefs.dispensaries : [],
            states: Array.isArray(prefs.states) ? prefs.states : []
          });
          
          // Apply location preferences if they exist
          if (prefs.zipcode || prefs.radius !== undefined) {
            setLocation({
              zipcode: prefs.zipcode || '',
              radius: prefs.radius !== undefined ? prefs.radius : 25
            });
          }
          
          // Apply sort preference if it exists and is valid for current tab
          if (prefs.sort_by) {
            // Only apply sort if it's valid for the current tab
            const validSorts = activeTab === 'flower' 
              ? ['cost_asc', 'cost_desc', 'tac_desc', 'thc_desc']
              : ['cost_asc', 'cost_desc'];
            if (validSorts.includes(prefs.sort_by)) {
              setSortBy(prefs.sort_by);
            }
          }
        }
      } catch (err) {
        // Silently fail - user can still use the page without preferences
        console.error('Failed to load user preferences:', err);
      } finally {
        setPreferencesLoaded(true);
      }
    };
    
    loadUserPreferences();
  }, [isAuthenticated, user, activeTab]); // activeTab dependency ensures reload when switching

  // Effects - simple data fetching - WAIT for preferences to load first
  useEffect(() => {
    // Don't fetch data until preferences have been loaded
    if (!preferencesLoaded) return;
    
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      const { endpoint, params } = buildApiParams(activeTab, filters, location, sortBy, limit, page);
      
      try {
        const result = await apiClient.get(endpoint, params);
        
        if (result.success) {
          // Capitalize brand names in the data
          const processedData = result.data.map(item => ({
            ...item,
            brand: capitalizeWords(item.brand)
          }));
          setData(processedData);
          setTotalCount(result.total || result.count);
          setTotalPages(result.total_pages || Math.ceil((result.total || result.count) / limit));
        } else {
          setError(result.error || 'Failed to fetch data');
          setData([]);
          setTotalCount(0);
          setTotalPages(1);
        }
      } catch (err) {
        // Always use safe error message to prevent exposing sensitive information
        setError(getSafeErrorMessage(err));
        
        setData([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    // Small delay to let UI update first
    const timer = setTimeout(() => {
      loadData();
    }, 50);
    
    return () => clearTimeout(timer);
  }, [activeTab, filters, location, sortBy, limit, page, buildApiParams, preferencesLoaded]); // Added preferencesLoaded dependency
  

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Top Navigation */}
      <div className="w-full bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-end">
            {/* User Account Section */}
            {isAuthenticated && user && (
              <div className="flex-shrink-0">
                <UserAccountMenu
                  user={user}
                  onManageSubscription={handleManageSubscription}
                  onLogout={handleLogout}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Main Content */}
          <div className="flex-1">
            {/* Header */}
            <div className="text-center mb-12">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-4">
                🍁 WEEDERBOARD.COM
              </h1>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-4">
                The Cannabis Deals Leaderboard
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
                Find the best deals on cannabis products across dispensaries
              </p>
            </div>

            {/* Premium Banner removed - all users are premium */}

            {/* Navigation Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-8 bg-white p-2 rounded-2xl shadow-lg max-w-2xl mx-auto">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => handleTabChange(id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeTab === id
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={18} />
                  <span>{label}</span>
                </button>
              ))}
            </div>


            {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                <div className="flex items-center space-x-2">
                  <label className="text-gray-700 font-medium text-sm">Per page:</label>
                  <div className="relative">
                    <select
                      value={limit}
                      onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                      className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-6 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <ChevronDown size={14} className="absolute right-2 top-2.5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <label className="text-gray-700 font-medium text-sm">Sort by:</label>
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-6 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-w-0"
                    >
                      {availableSortOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-2 top-2.5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(Math.max(1, page - 1))}
                      disabled={page === 1 || loading}
                      className="px-2 sm:px-3 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      Prev
                    </button>
                    <span className="text-gray-700 font-medium text-sm">
                      {page}/{totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages || loading}
                      className="px-2 sm:px-3 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      Next
                    </button>
                  </div>
                )}
                
                <button
                  onClick={fetchData}
                  disabled={loading}
                  className="px-4 sm:px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 disabled:opacity-50 text-sm sm:text-base"
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </div>

            {/* Filters Section */}
            <FastFilterPanel
              activeTab={activeTab}
              appliedFilters={filters}
              appliedLocation={location}
              appliedSortBy={sortBy}
              onApplyFilters={handleApplyFilters}
              onApplyLocation={handleLocationChange}
              isExpanded={filtersExpanded}
              onToggleExpanded={toggleFilters}
              isAuthenticated={isAuthenticated}
            />

            {/* Content */}
            {loading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
                <p className="mt-4 text-gray-600">Loading deals...</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mb-6">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {!loading && data.length > 0 && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {TABS.find(tab => tab.id === activeTab)?.label} - Best Deals
                  </h2>
                  <p className="text-gray-600">
                    Showing {data.length} of {totalCount} results
                    {totalPages > 1 && ` (page ${page} of ${totalPages})`}
                  </p>
                </div>

                {data.map((item, index) => {
                  const globalIndex = (page - 1) * limit + index;
                  return (
                    <LeaderboardItem 
                      key={item.id || `${item.strain}-${index}-${globalIndex}`} 
                      item={item} 
                      index={globalIndex}
                    />
                  );
                })}
                
                {/* Bottom Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-4 mt-8 py-4">
                    <button
                      onClick={() => handlePageChange(Math.max(1, page - 1))}
                      disabled={page === 1 || loading}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    <PaginationButtons
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      loading={loading}
                    />
                    
                    <button
                      onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages || loading}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}

            {!loading && data.length === 0 && totalCount > 0 && (
              <div className="text-center py-12">
                <p className="text-xl text-gray-500 mb-4">No deals match your current filters</p>
                <p className="text-gray-400">Try adjusting your filters to see more results</p>
                <button
                  onClick={clearAllFilters}
                  className="mt-4 px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200"
                >
                  Clear All Filters
                </button>
              </div>
            )}

            {!loading && data.length === 0 && totalCount === 0 && !error && (
              <div className="text-center py-12">
                <p className="text-xl text-gray-500 mb-4">No deals found</p>
                <p className="text-gray-400">Check back later for new deals</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Manager Modal */}
      {showSubscriptionManager && (
        <SubscriptionManager onClose={handleCloseSubscriptionManager} />
      )}
    </div>
  );
};

export default CannabisLeaderboard;
